import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'
import DefaultJurisdictionField from '@/jurisdiction-mappings/components/DefaultJurisdictionField'
import { JurisdictionKind, useJurisdictionFieldState } from '@/jurisdiction-mappings/jurisdiction-field-state'
import { useEffect, useState } from 'react'

type DefaultJurisdictionSettingsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialJurisdiction: string | null
  onSave: (jurisdiction: string | null) => Promise<void>
}

export default function DefaultJurisdictionSettingsDialog(props: DefaultJurisdictionSettingsDialogProps) {
  const { open, onOpenChange, initialJurisdiction, onSave } = props
  const jurisdictionField = useJurisdictionFieldState(initialJurisdiction, JurisdictionKind.STATE)
  const { reset: resetJurisdictionField } = jurisdictionField
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) {
      resetJurisdictionField()
      setSaveError(null)
      setIsSaving(false)
    }
    // resetJurisdictionField changes when initialJurisdiction changes; keep reset behavior in sync with the opened dialog.
  }, [open, resetJurisdictionField])

  const handleSave = async () => {
    const validationError = jurisdictionField.validate()
    if (validationError) {
      return
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      await onSave(jurisdictionField.value)
      onOpenChange(false)
    } catch {
      setSaveError('There was an error saving your default jurisdiction. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        title="Configure default jurisdiction"
        subtitle="Set the jurisdiction you commonly work in to speed up matter creation."
        width="md"
        height="max-sm"
        // Opened from within the SettingsModal, so stack above its overlay/content (repo's second-dialog pattern).
        stacked
        footer={
          <ModalFooterWithActions
            inlineMessage={saveError ? { variant: 'error', children: saveError } : undefined}
            secondaryAction={{ label: 'Cancel', onClick: () => onOpenChange(false), disabled: isSaving }}
            primaryAction={{ label: 'Save', onClick: handleSave, loading: isSaving, disabled: !jurisdictionField.value || isSaving }}
          />
        }
      >
        <DefaultJurisdictionField
          {...jurisdictionField.fieldState}
          idPrefix="default-jurisdiction"
          error={jurisdictionField.error ?? undefined}
          modal
          onChange={(nextState) => {
            jurisdictionField.onChange(nextState)
            setSaveError(null)
          }}
        />
      </ModalContent>
    </Modal>
  )
}
